import { useState, useEffect, useRef } from "react";
import fetchWithCredentials, { FetchWithCredentialsError } from "apiClient";
import Message from "types/Message";
import { ensureMessageListKey } from "utils/message";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  deferredMessages?: T[];
}

export function useChat(
  condoId: number | null,
  contactId: number | null,
  page: number
): {
  messages: Message[];
  loading: boolean;
  error: string | null;
  next: string | null;
  sendMessage: (
    content: string,
    condoId: number | null,
    fileUrl?: string | null
  ) => Promise<boolean>;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<string | null>(null);

  const previousContactIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!contactId) {
      setLoading(false);
      return;
    }

    // Check if contactId or page changed
    const contactIdChanged = previousContactIdRef.current !== contactId;

    // Update refs
    previousContactIdRef.current = contactId;

    if (contactIdChanged) {
      setMessages([]);
      setNext(null);
      setError(null);
    }

    // Always use page 1 when contactId changes, otherwise use the provided page
    const pageToFetch = contactIdChanged ? 1 : page;

    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append("page", String(pageToFetch));
        if (condoId != null) {
          params.append("condo", String(condoId));
        }
        const data: PaginatedResponse<Message> = await fetchWithCredentials(
          `/manager-api/contacts/${contactId}/chat/?${params.toString()}`
        );
        const results = data.results.map(ensureMessageListKey);
        const deferred = Array.isArray(data.deferredMessages)
          ? data.deferredMessages.map(ensureMessageListKey)
          : [];
        const merged =
          pageToFetch === 1 && deferred.length > 0
            ? [...results, ...deferred].sort(
                (a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt),
              )
            : results;
        setMessages(merged);
        setNext(data.next);
      } catch (err: any) {
        console.error("[useChat] Error fetching messages", err);
        const errorMessage = err.message || "Error fetching chat messages";
        // If it's a 404 and we're not on page 1, silently handle it (contact has fewer pages)
        if (errorMessage.includes("404") && pageToFetch > 1) {
          // Contact doesn't have this many pages, just set empty results
          setMessages([]);
          setNext(null);
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    // Always fetch when effect runs (on mount or when dependencies change)
    fetchMessages();
  }, [contactId, page, condoId]);

  const sendMessage = async (
    content: string,
    condoId: number | null,
    fileUrl?: string | null
  ): Promise<boolean> => {
    const body: any = { content, condoId };
    if (fileUrl) body.fileUrl = fileUrl;
    setError(null);
    try {
      await fetchWithCredentials(`/manager-api/contacts/${contactId}/chat/`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || "Error sending message";
      const statusCode =
        err instanceof FetchWithCredentialsError ? err.status : null;
      if (statusCode === null || statusCode < 500) {
        setError(errorMessage);
      }
      return false;
    }
  };

  return { messages, loading, error, next, sendMessage };
}
